import { useState } from "react";
import { Button } from "@radix-ui/themes";
import { Link, useLocation } from "react-router-dom";
import NavigationItem from "./NavigationItem";
import { useAuth } from "../../context/AuthContext";
import {
  CameraIcon,
  QuestionMarkCircleIcon,
  ClipboardDocumentIcon,
  ChatBubbleLeftRightIcon,
  FunnelIcon,
  HomeIcon,
  UserPlusIcon,
  UserIcon,
  CogIcon,
} from "@heroicons/react/24/solid";

interface SidebarContentProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

export default function SidebarContent({
  isCollapsed,
  onToggleCollapse,
}: SidebarContentProps) {
  const location = useLocation();
  const { user } = useAuth();

  function getDaysLeft(planStartedAt: string): number {
    const startDate = new Date(planStartedAt);
    const today = new Date();

    // Add 30 days to plan start date
    const nextReset = new Date(startDate);
    nextReset.setDate(nextReset.getDate() + 30);

    // Calculate difference in milliseconds
    const diffMs = nextReset.getTime() - today.getTime();

    // Convert to days, rounding up
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    // If negative or expired, return 0
    return diffDays > 0 ? diffDays : 0;
  }

  return (
    <div className="border-r border-gray-200 dark:border-gray-800 flex flex-col h-full relative bg-white dark:bg-gray-950 text-gray-900 dark:text-white transition-colors duration-200">
      {/* Logo */}
      <div className="p-4 flex items-center">
        <img
          src="/logo.svg"
          alt="ugz.ai logo"
          className="w-8 h-8 transition-colors duration-200"
        />
        {!isCollapsed && (
          <span className="font-semibold text-lg transition-colors duration-200">
            ugz.ai
          </span>
        )}
      </div>
      <div className="border-b border-gray-200 dark:border-gray-800 w-full mb-4"></div>

      {/* Main content with flex to separate top and bottom sections */}
      <div className="flex flex-col flex-1 justify-between h-full">
        {/* Top section - Features */}
        <div className="px-4 mt-4">
          {/* Features Section */}
          {!isCollapsed && (
            <div className="text-gray-500 dark:text-gray-400 text-sm mb-2 transition-colors duration-200">
              Features
            </div>
          )}
          <nav className="space-y-1 flex-1">
            {/* Dashboard */}
            <NavigationItem
              to="/dashboard"
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 512 512"
                  className="h-5 w-5"
                >
                  {/*!Font Awesome Free 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2025 Fonticons, Inc.*/}
                  <path d="M0 96C0 60.7 28.7 32 64 32l384 0c35.3 0 64 28.7 64 64l0 320c0 35.3-28.7 64-64 64L64 480c-35.3 0-64-28.7-64-64L0 96zm64 64l0 256 160 0 0-256L64 160zm384 0l-160 0 0 256 160 0 0-256z" />
                </svg>
              }
              label="Dashboard"
              isCollapsed={isCollapsed}
              isActive={location.pathname === "/dashboard"}
            />
            {/* Product Videos */}
            <NavigationItem
              showPlusIcon={true}
              to="/product-videos"
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 448 512"
                  className="h-5 w-5"
                >
                  {/*!Font Awesome Free 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2025 Fonticons, Inc.*/}
                  <path d="M50.7 58.5L0 160l208 0 0-128L93.7 32C75.5 32 58.9 42.3 50.7 58.5zM240 160l208 0L397.3 58.5C389.1 42.3 372.5 32 354.3 32L240 32l0 128zm208 32L0 192 0 416c0 35.3 28.7 64 64 64l320 0c35.3 0 64-28.7 64-64l0-224z" />
                </svg>
              }
              label="Product Videos"
              isCollapsed={isCollapsed}
              isActive={location.pathname === "/product-videos"}
            />

            {/* Talking Videos */}
            <NavigationItem
              to="/talking-videos"
              showPlusIcon={true}
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 384 512"
                  className="h-5 w-5"
                >
                  {/*!Font Awesome Free 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2025 Fonticons, Inc.*/}
                  <path d="M192 0C139 0 96 43 96 96l0 160c0 53 43 96 96 96s96-43 96-96l0-160c0-53-43-96-96-96zM64 216c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 40c0 89.1 66.2 162.7 152 174.4l0 33.6-48 0c-13.3 0-24 10.7-24 24s10.7 24 24 24l72 0 72 0c13.3 0 24-10.7 24-24s-10.7-24-24-24l-48 0 0-33.6c85.8-11.7 152-85.3 152-174.4l0-40c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 40c0 70.7-57.3 128-128 128s-128-57.3-128-128l0-40z" />
                </svg>
              }
              label="Normal Videos"
              isCollapsed={isCollapsed}
              isActive={location.pathname === "/talking-videos"}
            />
            {/* Hook Videos */}
            {/* <NavigationItem
              to="/hook-videos"
              showPlusIcon={true}
              icon={
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 15 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                >
                  <path
                    d="M7.26562 0.109375C11.25 0.109375 14.5312 3.39062 14.5312 7.375C14.5312 11.3887 11.25 14.6406 7.26562 14.6406C3.25195 14.6406 0 11.3887 0 7.375C0 3.39062 3.25195 0.109375 7.26562 0.109375ZM7.26562 12.7656C10.2246 12.7656 12.6562 10.3633 12.6562 7.375C12.6562 4.41602 10.2246 1.98438 7.26562 1.98438C4.27734 1.98438 1.875 4.41602 1.875 7.375C1.875 10.3633 4.27734 12.7656 7.26562 12.7656ZM7.26562 3.625C9.31641 3.625 11.0156 5.32422 11.0156 7.375C11.0156 9.45508 9.31641 11.125 7.26562 11.125C5.18555 11.125 3.51562 9.45508 3.51562 7.375C3.51562 5.32422 5.18555 3.625 7.26562 3.625ZM7.26562 9.25C8.29102 9.25 9.14062 8.42969 9.14062 7.375C9.14062 6.34961 8.29102 5.5 7.26562 5.5C6.21094 5.5 5.39062 6.34961 5.39062 7.375C5.39062 8.42969 6.21094 9.25 7.26562 9.25Z"
                    fill="currentColor"
                  />
                </svg>
              }
              label="Hook Videos"
              isCollapsed={isCollapsed}
              isActive={location.pathname === "/hook-videos"}
            /> */}
            {/* Talking Videos */}
            <NavigationItem
              to="/pricing"
              showPlusIcon={false}
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="size-6"
                >
                  <path d="M12 7.5a2.25 2.25 0 1 0 0 4.5 2.25 2.25 0 0 0 0-4.5Z" />
                  <path
                    fillRule="evenodd"
                    d="M1.5 4.875C1.5 3.839 2.34 3 3.375 3h17.25c1.035 0 1.875.84 1.875 1.875v9.75c0 1.036-.84 1.875-1.875 1.875H3.375A1.875 1.875 0 0 1 1.5 14.625v-9.75ZM8.25 9.75a3.75 3.75 0 1 1 7.5 0 3.75 3.75 0 0 1-7.5 0ZM18.75 9a.75.75 0 0 0-.75.75v.008c0 .414.336.75.75.75h.008a.75.75 0 0 0 .75-.75V9.75a.75.75 0 0 0-.75-.75h-.008ZM4.5 9.75A.75.75 0 0 1 5.25 9h.008a.75.75 0 0 1 .75.75v.008a.75.75 0 0 1-.75.75H5.25a.75.75 0 0 1-.75-.75V9.75Z"
                    clipRule="evenodd"
                  />
                  <path d="M2.25 18a.75.75 0 0 0 0 1.5c5.4 0 10.63.722 15.6 2.075 1.19.324 2.4-.558 2.4-1.82V18.75a.75.75 0 0 0-.75-.75H2.25Z" />
                </svg>
              }
              label="Pricing"
              isCollapsed={isCollapsed}
              isActive={location.pathname === "/pricing"}
            />
            {/* <NavigationItem
              to="/hook-videos"
              showPlusIcon={true}
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 512 512"
                  className="h-5 w-5"
                >
                  <path d="M448 256A192 192 0 1 0 64 256a192 192 0 1 0 384 0zM0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256zm256 80a80 80 0 1 0 0-160 80 80 0 1 0 0 160zm0-224a144 144 0 1 1 0 288 144 144 0 1 1 0-288zM224 256a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z" />
                </svg>
              }
              label="Hook Videos"
              isCollapsed={isCollapsed}
              isActive={location.pathname === "/hook-videos"}
            /> */}
          </nav>
        </div>

        {/* Bottom section - Misc and Videos Remaining */}
        <div className="px-4 mb-4 space-y-4">
          {/* Misc Section */}
          <div className="pb-5">
            {!isCollapsed && (
              <div className="text-gray-500 dark:text-gray-400 text-sm mb-2 transition-colors duration-200">
                Misc
              </div>
            )}
            <nav className="space-y-1">
              {/* FAQ */}
              <NavigationItem
                to="/faq"
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                    className="h-5 w-5"
                  >
                    {/*!Font Awesome Free 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2025 Fonticons, Inc.*/}
                    <path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM169.8 165.3c7.9-22.3 29.1-37.3 52.8-37.3l58.3 0c34.9 0 63.1 28.3 63.1 63.1c0 22.6-12.1 43.5-31.7 54.8L280 264.4c-.2 13-10.9 23.6-24 23.6c-13.3 0-24-10.7-24-24l0-13.5c0-8.6 4.6-16.5 12.1-20.8l44.3-25.4c4.7-2.7 7.6-7.7 7.6-13.1c0-8.4-6.8-15.1-15.1-15.1l-58.3 0c-3.4 0-6.4 2.1-7.5 5.3l-.4 1.2c-4.4 12.5-18.2 19-30.6 14.6s-19-18.2-14.6-30.6l.4-1.2zM224 352a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z" />
                  </svg>
                }
                label="FAQ"
                isCollapsed={isCollapsed}
                isActive={location.pathname === "/faq"}
              />

              {/* Admin links only visible to admin users */}
              {user?.email &&
                [
                  "mikay777mm@gmail.com",
                  "nevinpuri1901@gmail.com",
                  "victor.f.chapman@gmail.com",
                ].includes(user.email) && (
                  <>
                    <NavigationItem
                      to="/admin-jobs"
                      icon={
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="w-5 h-5"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0 0 16.5 9h-1.875a1.875 1.875 0 0 1-1.875-1.875V5.25A3.75 3.75 0 0 0 9 1.5H5.625ZM7.5 15a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 7.5 15Zm.75 2.25a.75.75 0 0 0 0 1.5h7.5a.75.75 0 0 0 0-1.5h-7.5Z"
                            clipRule="evenodd"
                          />
                          <path d="M12.971 1.816A5.23 5.23 0 0 1 14.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 0 1 3.434 1.279 9.768 9.768 0 0 0-6.963-6.963Z" />
                        </svg>
                      }
                      label="Admin Jobs"
                      isCollapsed={isCollapsed}
                      isActive={location.pathname === "/admin-jobs"}
                    />

                    <NavigationItem
                      to="/video-feedback"
                      icon={
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="w-5 h-5"
                        >
                          <path
                            fillRule="evenodd"
                            d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
                            clipRule="evenodd"
                          />
                        </svg>
                      }
                      label="Video Feedback"
                      isCollapsed={isCollapsed}
                      isActive={location.pathname === "/video-feedback"}
                    />
                  </>
                )}

              {/* Discord */}
              {/* <NavigationItem
                to="/discord"
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 640 512"
                    className="h-5 w-5"
                  >
                    <path d="M524.5 69.8a1.5 1.5 0 0 0 -.8-.7A485.1 485.1 0 0 0 404.1 32a1.8 1.8 0 0 0 -1.9 .9 337.5 337.5 0 0 0 -14.9 30.6 447.8 447.8 0 0 0 -134.4 0 309.5 309.5 0 0 0 -15.1-30.6 1.9 1.9 0 0 0 -1.9-.9A483.7 483.7 0 0 0 116.1 69.1a1.7 1.7 0 0 0 -.8 .7C39.1 183.7 18.2 294.7 28.4 404.4a2 2 0 0 0 .8 1.4A487.7 487.7 0 0 0 176 479.9a1.9 1.9 0 0 0 2.1-.7A348.2 348.2 0 0 0 208.1 430.4a1.9 1.9 0 0 0 -1-2.6 321.2 321.2 0 0 1 -45.9-21.9 1.9 1.9 0 0 1 -.2-3.1c3.1-2.3 6.2-4.7 9.1-7.1a1.8 1.8 0 0 1 1.9-.3c96.2 43.9 200.4 43.9 295.5 0a1.8 1.8 0 0 1 1.9 .2c2.9 2.4 6 4.9 9.1 7.2a1.9 1.9 0 0 1 -.2 3.1 301.4 301.4 0 0 1 -45.9 21.8 1.9 1.9 0 0 0 -1 2.6 391.1 391.1 0 0 0 30 48.8 1.9 1.9 0 0 0 2.1 .7A486 486 0 0 0 610.7 405.7a1.9 1.9 0 0 0 .8-1.4C623.7 277.6 590.9 167.5 524.5 69.8zM222.5 337.6c-29 0-52.8-26.6-52.8-59.2S193.1 219.1 222.5 219.1c29.7 0 53.3 26.8 52.8 59.2C275.3 311 251.9 337.6 222.5 337.6zm195.4 0c-29 0-52.8-26.6-52.8-59.2S388.4 219.1 417.9 219.1c29.7 0 53.3 26.8 52.8 59.2C470.7 311 447.5 337.6 417.9 337.6z" />
                  </svg>
                }
                label="Discord"
                isCollapsed={isCollapsed}
                isActive={location.pathname === "/discord"}
              />

              <NavigationItem
                to="/refer"
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 576 512"
                    className="h-5 w-5"
                  >
                    <path d="M64 64C28.7 64 0 92.7 0 128L0 384c0 35.3 28.7 64 64 64l448 0c35.3 0 64-28.7 64-64l0-256c0-35.3-28.7-64-64-64L64 64zm64 320l-64 0 0-64c35.3 0 64 28.7 64 64zM64 192l0-64 64 0c0 35.3-28.7 64-64 64zM448 384c0-35.3 28.7-64 64-64l0 64-64 0zm64-192c-35.3 0-64-28.7-64-64l64 0 0 64zM288 160a96 96 0 1 1 0 192 96 96 0 1 1 0-192z" />
                  </svg>
                }
                label="Refer get $1,000"
                isCollapsed={isCollapsed}
                isActive={location.pathname === "/refer"}
              /> */}
            </nav>
          </div>

          {/* Videos Remaining Section */}
          <div className="bg-blue-50/80 dark:bg-blue-900/10 rounded-lg p-3 border-[0.5px] border-blue-100 dark:border-blue-800/50 shadow-sm transition-colors duration-200">
            <Link to="/pricing">
              <div>
                <div className="text-blue-700 dark:text-blue-300 font-semibold text-sm transition-colors duration-200">
                  {!isCollapsed
                    ? `${user?.videos_left || 0} credits remaining`
                    : `${user?.videos_left || 0}`}
                </div>
                {/* {!isCollapsed && (
                  <div className="text-blue-600/70 dark:text-blue-400/70 text-xs font-semibold transition-colors duration-200">
                    Resets in {getDaysLeft(user?.plan_started_at || "")} days
                  </div>
                )} */}
              </div>
              <div className="bg-blue-100/70 dark:bg-blue-800/20 h-1.5 rounded-full overflow-hidden mt-2 transition-colors duration-200">
                <div
                  className="bg-blue-500 dark:bg-blue-600 h-full rounded-full transition-all duration-300 shadow-[0_0_8px_rgba(59,130,246,0.5)] dark:shadow-[0_0_8px_rgba(37,99,235,0.5)]"
                  style={{
                    width: `${((user?.videos_left || 16) / 40) * 100}%`,
                  }}
                ></div>
              </div>
            </Link>
          </div>

          {/* User Profile Section */}
          <div className="flex items-center py-3 px-2">
            <div className="flex items-center w-full justify-between">
              <div className="flex items-center gap-3">
                {user?.picture ? (
                  <img
                    src={user.picture}
                    alt="User profile"
                    className="w-10 h-10 rounded-md border border-gray-200 dark:border-gray-700 object-cover transition-colors duration-200"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-md bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 font-bold transition-colors duration-200">
                    {user?.name?.charAt(0) || user?.email?.charAt(0) || "U"}
                  </div>
                )}
                {!isCollapsed && (
                  <div className="flex flex-col">
                    <div className="font-medium text-gray-900 dark:text-white truncate transition-colors duration-200">
                      {user?.name || "shadcn"}
                    </div>
                    {/* <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                    {user?.email || "m@example.com"}
                  </div> */}
                  </div>
                )}
              </div>
              <Link
                to="/account"
                className="text-gray-500 dark:text-gray-400 relative group z-50"
                title="Account Settings"
              >
                <CogIcon className="size-6" />
                <span className="absolute left-full ml-2 whitespace-nowrap bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  Account Settings
                </span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
